'use client';

import { KeyCodes } from '@coolblue-development/constants';
import { Box, Button, Flex, Input, Typography, VisuallyHidden } from '@coolblue-development/freddy';
import confirmPriceFilterTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/confirmPriceFilterTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { getPlaceholder } from '@features/filter/components/Filters/RangeFacet/getPlaceholder';
import { FilterPageContext } from '@features/filter/context';
import { useFacetValue } from '@features/filter/hooks/useFacetValue';
import { useTranslation } from '@i18n/useTranslation';
import type { EventFor } from '@utils/type-utils';
import { useContext, useState } from 'react';
import { coerce, literal, z } from 'zod';

import type { RangeFacet } from '../../../types';

const preferredUnit = '€';
const facetLabelId = 'price-facet-label';
const ENTER_KEY = KeyCodes.ENTER;
type KeyDownEvent = EventFor<'input', 'onKeyDown'>;

export default function RangeFacet({ facet }: { facet: RangeFacet }) {
  const { t } = useTranslation('webshop');
  const { setFilter, trackingSubFeature } = useContext(FilterPageContext);

  const initialLowerBound =
    facet.selectedLowerBound === facet.minValue ? '' : String(facet.selectedLowerBound);
  const initialUpperBound =
    facet.selectedUpperBound === facet.maxValue ? '' : String(facet.selectedUpperBound);
  const initialValues = [String(initialLowerBound), String(initialUpperBound)];

  const [currentFacetValue, setCurrentFacetValue] = useFacetValue(initialValues, ['']);
  const [minValue, maxValue] = currentFacetValue;

  function setMinValue(value: string) {
    setCurrentFacetValue([value, maxValue]);
  }
  function setMaxValue(value: string) {
    setCurrentFacetValue([minValue, value]);
  }

  const [validation, setValidation] = useState({
    minFieldValid: true,
    maxFieldValid: true,
    error: ''
  });

  const [minPlaceholder, maxPlaceholder] = getPlaceholder(facet.minValue, facet.maxValue);

  const { trackEvent } = useAnalytics();
  const validationMessageId = `${facet.name}-error`;

  function updateFilter(values: string[]) {
    const shouldScroll = true;
    setFilter?.({ name: facet.name, values }, shouldScroll);
  }

  function validate(): boolean {
    const minSchema = coerce.number().max(facet.maxValue).optional().or(literal(''));
    const minFieldResult = minSchema.safeParse(minValue);
    const maxSchema = coerce.number().min(facet.minValue).optional().or(literal(''));
    const maxFieldResult = maxSchema.safeParse(maxValue);

    const schema = z
      .object({
        low: z.coerce.number().max(facet.maxValue).optional().or(literal('')),
        high: z.coerce.number().min(facet.minValue).optional().or(literal(''))
      })
      .refine(data => {
        const low = Number(data.low);
        const high = Number(data.high);

        if (low > 0 && high > 0) {
          return low < high;
        }
        return true;
      });

    const result = schema.safeParse({ low: minValue, high: maxValue });
    setValidation({
      minFieldValid: minFieldResult.success,
      maxFieldValid: maxFieldResult.success,
      error: result.success
        ? ''
        : t('filterPage.facets.rangeValidationError', {
            min: facet.minValue,
            max: facet.maxValue,
            unit: preferredUnit
          })
    });

    return result.success;
  }

  function handleSubmit() {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    if (!minValue.length && !maxValue.length) {
      return;
    }
    const filterValue = `${minValue || facet.minValue}-${maxValue || facet.maxValue}`;
    const params = {
      sub_feature: trackingSubFeature,
      filter_value: filterValue,
      number_of_items: 1,
      is_recommended: true
    } as const;
    trackEvent(confirmPriceFilterTrigger(params));
    setCurrentFacetValue([minValue, maxValue]);
    updateFilter([filterValue]);
  }

  function handleKeyDown(event: KeyDownEvent) {
    if (event.key === ENTER_KEY) {
      handleSubmit();
    }
  }

  return (
    <Flex direction='column' mt={300} gap={100} role='group' aria-labelledby={facetLabelId}>
      <VisuallyHidden>
        <Box component='span' id={facetLabelId}>
          {t('filterPage.facets.priceRangeLabel')}
        </Box>
      </VisuallyHidden>

      <Flex gap={200}>
        <RangeInput
          idSuffix='min'
          value={minValue}
          setValue={setMinValue}
          isValid={validation.minFieldValid}
          placeholder={minPlaceholder}
          describedById={validationMessageId}
          label={t('filterPage.facets.minimumPrice')}
          facet={facet}
          handleKeyDown={handleKeyDown}
        />

        <Box alignSelf='center'>{t('filterPage.facets.rangeFilterText')}</Box>

        <RangeInput
          idSuffix='max'
          value={maxValue}
          setValue={setMaxValue}
          isValid={validation.maxFieldValid}
          placeholder={maxPlaceholder}
          describedById={validationMessageId}
          label={t('filterPage.facets.maximumPrice')}
          facet={facet}
          handleKeyDown={handleKeyDown}
        />

        <Button
          variant='secondary'
          aria-label={t('filterPage.facets.ariaLabel', { displayName: facet.displayName })}
          size='small'
          onClick={handleSubmit}
        >
          <ArrowRight />
        </Button>
      </Flex>

      {validation.error && (
        <Typography component='span' color='sentimentDanger' id={validationMessageId}>
          {validation.error}
        </Typography>
      )}
    </Flex>
  );
}

type RangeInputProps = {
  idSuffix: 'min' | 'max';
  value: string;
  setValue: (val: string) => void;
  isValid: boolean;
  placeholder: number;
  describedById: string;
  label: string;
  facet: RangeFacet;
  handleKeyDown: (event: KeyDownEvent) => void;
};

function RangeInput({
  idSuffix,
  value,
  setValue,
  isValid,
  placeholder,
  describedById,
  label,
  facet,
  handleKeyDown
}: RangeInputProps) {
  return (
    <Box sx={{ width: '8rem' }}>
      <Input
        type='number'
        name={`${facet.name}-${idSuffix}`}
        id={`${facet.name}-${idSuffix}`}
        aria-label={label}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        min={idSuffix === 'max' ? facet.minValue : undefined}
        max={idSuffix === 'min' ? facet.maxValue : undefined}
        placeholder={String(placeholder)}
        validation={!isValid ? 'error' : undefined}
        aria-describedby={isValid ? describedById : undefined}
        startAdornment={preferredUnit}
        sx={{
          '[type="number"]': { MozAppearance: 'textfield' },
          '[type="number"]::-webkit-inner-spin-button,[type="number"]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0
          }
        }}
      />
    </Box>
  );
}
